import { bytesToNDigits } from '@mail/cross-sizes-utils';
import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BASEQUOTA } from 'reactApp/appHelpers/configHelpers';
import { cardUnlinkOutflowTouch } from 'reactApp/appHelpers/featuresHelpers/features/cardUnlinkOutflow';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
/* tempexp_20958-start */
import { getActiveWebSubscription } from 'reactApp/hooks/subscriptionCommunication.helpers';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { loadHomeFolderRequest } from 'reactApp/modules/home/home.actions';
import { getHomeFolderItems } from 'reactApp/modules/home/home.selectors';
import { SubscriptionRenewalActions } from 'reactApp/modules/subscriptionRenewal/subscriptionRenewal.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { SUBSCRIPTION_ANALYTICS } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscription.analytics';
import { BazModalContent, BazModalHeader } from 'reactApp/sections/SubscriptionsPage/ui/CardUnlinkBazModal/CardUnlinkBazModalContent';
import {
    TrialModalContent,
    TrialModalHeader,
} from 'reactApp/sections/SubscriptionsPage/ui/CardUnlinkTrialModal/CardUnlinkTrialModalContent';
import { getBazFilesSize, sendCardUnlinkOutflowAnalytics } from 'reactApp/sections/SubscriptionsPage/ui/CreditCard/CreditCard.helpers';
import { useSendSubscriptionModalAnalytics } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import type { RootState } from 'reactApp/store';
import type { HardlyUsedThunkDispatch } from 'reactApp/types/app';
import { closeOverquotaFilesModal, openOverquotaFilesModal } from 'reactApp/ui/OverquotaFilesModal/OverquotaFilesModal';
import { closeOverquotaWarningModal, openOverquotaWarningModal } from 'reactApp/ui/OverquotaWarningModal/OverquotaWarningModal';
import {
    closeUnlinkCardConfirmModal,
    renderUnlinkCardConfirmModal,
} from 'reactApp/ui/UnlinkCardConfirmModal/UnlinkCardConfirmModal.helpers';
import { sendDwh } from 'reactApp/utils/ga';
/* tempexp_20958-end */

export const useHandlers = (subscription: BillingDataSubscriptionInfo, onClose: () => void) => {
    const dispatch = useDispatch<HardlyUsedThunkDispatch>();
    const { expires, productId, start, partner } = subscription;
    /* tempexp_20958-next-line */
    const trialSubscription = useSelector(BillingDataSelectors.getAutorenewalTrialSubscription);
    /* tempexp_20958-start */
    const hasUnlimitedUpload = useSelector(BillingDataSelectors.getSubscriptionFeatures).unlimitedUpload;
    const usedSpace = useSelector(UserQuotaSelectors.getUsed)?.total?.original ?? 0;
    const activeSubscription = useSelector((state: RootState) =>
        getActiveWebSubscription(BillingDataSelectors.getSubscriptionsList(state))
    );
    const hasHomeItems = useSelector((state: RootState) => getHomeFolderItems(state).length > 0);
    const bazSizePromiseRef = useRef<Promise<number> | null>(null);
    /* tempexp_20958-end */

    const dwhData = useMemo(
        () => ({
            product: productId,
            start_date: start,
            expired_date: expires,
            source: 'touch',
            plarform: String(EnvironmentSelectors.getMobileType()).toLowerCase(),
        }),
        [expires, productId, start]
    );

    const handleUpdateCard = useCallback(() => {
        dispatch(
            SubscriptionRenewalActions.addOrUpdateCardMobile({
                dwhData,
                closeSubscriptionCard: onClose,
            })
        );
    }, [dispatch, dwhData, onClose]);

    const sendAnalytics = useSendSubscriptionModalAnalytics(subscription);

    const handleCancelNextPayment = useCallback(
        (isRebrandingQuotaLanding?: boolean, isRebranding?: boolean) => {
            dispatch(
                SubscriptionRenewalActions.cancelAutoRenewMobile({
                    subscription,
                    dwhData,
                    sendAnalytics,
                    closeSubscriptionCard: onClose,
                    isRebranding,
                    isRebrandingQuotaLanding,
                })
            );
        },
        [dispatch, subscription, dwhData, onClose, sendAnalytics]
    );

    const handleRenewNextPayment = useCallback(() => {
        dispatch(
            SubscriptionRenewalActions.renewMobile({
                closeSubscriptionCard: onClose,
                dwhData,
                subscription,
            })
        );
    }, [dispatch, dwhData, subscription, onClose]);

    const handleGoToSubscription = useCallback(() => {
        sendDwh({
            ...SUBSCRIPTION_ANALYTICS.MY_TARIFF_ADD_SPACE,
            dwhData,
        });

        onClose();
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, [dwhData, onClose]);

    const handlePartnerClick = useCallback(() => {
        sendDwh({
            ...SUBSCRIPTION_ANALYTICS.MY_TARIFF_PARTNER_CLICK,
            dwhData: {
                parner_name: partner,
                source: 'touch',
            },
        });
    }, [partner]);

    /* tempexp_20958-start */
    const removeCard = useCallback(() => {
        dispatch(CardActions.removeCard({}));
    }, [dispatch]);

    const showOverquotaFilesModal = useCallback(() => {
        if (!activeSubscription) {
            removeCard();
            return;
        }

        if (!hasHomeItems) {
            dispatch(loadHomeFolderRequest({ id: ROOT_FOLDER_ID, isFolder: true }));
        }

        openOverquotaFilesModal({
            subscription: activeSubscription,
            isCardUnlink: true,
            onCancel: () => {
                closeOverquotaFilesModal();
                removeCard();
            },
            onClose: closeOverquotaFilesModal,
        });
    }, [activeSubscription, dispatch, hasHomeItems, removeCard]);

    const handleAfterBaz = useCallback(
        (bazSize: number) => {
            if (!activeSubscription || usedSpace + bazSize <= BASEQUOTA) {
                removeCard();
                return;
            }

            openOverquotaWarningModal({
                subscription: activeSubscription,
                isCardUnlink: true,
                bazSize,
                onCancel: () => {
                    closeOverquotaWarningModal();
                    showOverquotaFilesModal();
                },
                onClose: closeOverquotaWarningModal,
            });
        },
        [activeSubscription, removeCard, showOverquotaFilesModal, usedSpace]
    );

    const showBazModal = useCallback(async () => {
        let bazSize = 0;
        try {
            bazSize = (await bazSizePromiseRef.current) ?? 0;
        } catch {
            // если не удалось получить размер — пропускаем шаг
        }

        if (bazSize === 0) {
            handleAfterBaz(0);
            return;
        }

        const isOverBaseQuotaWithBaz = usedSpace + bazSize > BASEQUOTA;
        const bazSizeFormatted = bytesToNDigits(bazSize, 3).value;
        const analyticsName = isOverBaseQuotaWithBaz ? 'mnz_card_unlink_outflow_baz_overquota' : 'mnz_card_unlink_outflow_baz';

        renderUnlinkCardConfirmModal({
            actionButtonText: 'Всё равно отвязать',
            dataQAId: 'unlink-card-baz-confirm-modal',
            renderHeader: () => <BazModalHeader bazSizeFormatted={bazSizeFormatted} isOverBaseQuotaWithBaz={isOverBaseQuotaWithBaz} />,
            renderContent: () => <BazModalContent isOverBaseQuotaWithBaz={isOverBaseQuotaWithBaz} />,
            sendAnalytics: (action, buttonName) => sendCardUnlinkOutflowAnalytics(action, analyticsName, buttonName, 'touch'),
            onClose: closeUnlinkCardConfirmModal,
            onUnlink: () => {
                closeUnlinkCardConfirmModal();
                handleAfterBaz(bazSize);
            },
        });
    }, [handleAfterBaz, usedSpace]);

    const handleAfterTrial = useCallback(() => {
        if (hasUnlimitedUpload) {
            showBazModal();
            return;
        }
        handleAfterBaz(0);
    }, [handleAfterBaz, hasUnlimitedUpload, showBazModal]);

    const handleRemoveCard = useCallback(() => {
        if (!cardUnlinkOutflowTouch) {
            return;
        }

        if (hasUnlimitedUpload) {
            bazSizePromiseRef.current = getBazFilesSize();
        }

        onClose();

        if (trialSubscription && trialSubscription.isTrial) {
            renderUnlinkCardConfirmModal({
                actionButtonText: 'Всё равно отвязать',
                dataQAId: 'unlink-card-trial-confirm-modal',
                renderHeader: () => <TrialModalHeader expires={trialSubscription.expires} />,
                renderContent: () => <TrialModalContent space={trialSubscription.space} />,
                sendAnalytics: (action, buttonName) =>
                    sendCardUnlinkOutflowAnalytics(action, 'mnz_card_unlink_outflow_trial', buttonName, 'touch'),
                onClose: closeUnlinkCardConfirmModal,
                onUnlink: () => {
                    closeUnlinkCardConfirmModal();
                    handleAfterTrial();
                },
            });
            return;
        }

        handleAfterTrial();
    }, [hasUnlimitedUpload, handleAfterTrial, onClose, trialSubscription]);
    /* tempexp_20958-end */

    return {
        handleUpdateCard,
        handleCancelNextPayment,
        handleGoToSubscription,
        handleRenewNextPayment,
        handlePartnerClick,
        // tempexp_20958-next-line
        handleRemoveCard,
    };
};
